
import React from 'react';
import { connect } from 'react-redux';
import { withTranslation } from "react-i18next";
import { Table, Button } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';

import * as types from 'reducer/actionTypes';
import { extractWebFontInfo } from 'script/fontExtractor';

const LOCALES = [
	{ key: 'ko', label: 'Korean (한국어)' },
	{ key: 'en', label: 'English' },
];

const _allowedExtensions = ['woff', 'woff2'];
const _allowedAccept = _allowedExtensions.map((ext) => `.${ext}`).join(',');
const _maxFileSize = 10 * 1024 * 1024; // 10MB

class SettingWebFont extends React.Component {

	state = {
		dialogOpen: false,
		selectedFile: null,
		errorMsg: null,
	};

	// 라이선스 적용 다이얼로그와 동일하게 native file input 사용 — 재선택을 위해 ref 로 value 초기화
	fileInputRef = React.createRef();

	componentDidMount() {
		this.props.fetchWebFontList();
	}

	resetFileInput = () => {
		if (this.fileInputRef.current) {
			this.fileInputRef.current.value = '';
		}
	}

	handleOpenDialog = () => {
		this.resetFileInput();
		this.setState({ dialogOpen: true, selectedFile: null, errorMsg: null });
	}

	handleCancelDialog = () => {
		this.resetFileInput();
		this.setState({ dialogOpen: false, selectedFile: null, errorMsg: null });
	}

	handleFileChange = (e) => {
		const file = e.target.files && e.target.files[0];
		if (!file) {
			this.setState({ selectedFile: null, errorMsg: null });
			return;
		}
		const fileExtension = file.name.split('.').pop().toLowerCase();
		if (!_allowedExtensions.includes(fileExtension)) {
			this.setState({ selectedFile: null, errorMsg: this.props.t('webfont.msg.invalidFormat') });
			return;
		}
		if (file.size > _maxFileSize) {
			this.setState({ selectedFile: null, errorMsg: this.props.t('webfont.msg.fileSizeExceeded') });
			return;
		}
		this.setState({ selectedFile: file, errorMsg: null });
	}

	handleApply = async (e) => {
		if (e) {
			e.preventDefault();
		}
		const { selectedFile } = this.state;
		if (!selectedFile) {
			return;
		}
		try {
			const { localeInfo } = await extractWebFontInfo(selectedFile);
			this.props.uploadWebFont(selectedFile, localeInfo);
			this.resetFileInput();
			this.setState({ dialogOpen: false, selectedFile: null, errorMsg: null });
		} catch (err) {
			this.setState({ errorMsg: this.props.t('webfont.msg.parseFailed') });
		}
	}

	handleDelete = (fontId) => {
		this.props.deleteWebFont(fontId);
	}

	render() {
		const { t, webFontData } = this.props;
		const { dialogOpen, selectedFile, errorMsg } = this.state;

		const columns = [
			{
				title: t('webfont.table.webFontName'),
				dataIndex: 'fileName',
				key: 'fileName',
				width: '25%',
			},
			{
				title: t('webfont.table.supportedLanguage'),
				key: 'localeInfo',
				render: (_, record) => {
					const localeInfo = record.localeInfo || {};
					const entries = LOCALES.filter(({ key }) => localeInfo[key]);
					if (entries.length === 0) {
						return <span className="webfont-locale-empty">-</span>;
					}
					return (
						<span>
							{entries.map(({ key }, index) => (
								<span key={key}>
									{index > 0 && <span className="webfont-locale-separator">·</span>}
									<span className="webfont-locale-key">{key}:</span>
									{' '}
									<strong>{localeInfo[key]}</strong>
								</span>
							))}
						</span>
					);
				}
			},
			{
				title: t('webfont.table.delete'),
				key: 'delete',
				width: '10%',
				align: 'center',
				render: (_, record) => (
					<Button
						icon={<DeleteOutlined />}
						onClick={() => this.handleDelete(record.fontId)}
					/>
				)
			}
		];

		return (
			<div className="webfont-container">
				<div className="webfont-header">
					<h2>{t('webfont.title')}</h2>
				</div>

				<Table
					bordered
					columns={columns}
					dataSource={webFontData}
					rowKey="fontId"
					pagination={false}
					locale={{ emptyText: t('webfont.table.empty') }}
				/>

				<div className="webfont-actions">
					<Button type="primary" onClick={this.handleOpenDialog}>
						{t('webfont.button.addWebFont')}
					</Button>
				</div>

				{/* 라이선스 적용 탭(License.js)의 LicenseUpdate 다이얼로그와 동일한 구조/클래스 사용 */}
				<div className="modal_overlay" style={{ display: dialogOpen ? 'block' : 'none' }}>
					<div className="modal_dialog" style={{ display: dialogOpen ? 'block' : 'none' }}>
						<form className="form" onSubmit={this.handleApply} encType="multipart/form-data">
							<div className="dialog">
								<div className="dialog_tl">{t('webfont.button.addWebFont')}</div>
								<div className="dialog_content">
									<div className="section_center">
										<div className="item_label">{t('webfont.title')}</div>
										<label htmlFor="webfont_file">
											<div className={selectedFile ? 'file_input' : 'file_input file_input_placeholder'} id="webfont_label_file">
												{selectedFile ? selectedFile.name : t('webfont.msg.validFormat')}
											</div>
											<input
												type="file"
												id="webfont_file"
												ref={this.fileInputRef}
												accept={_allowedAccept}
												onChange={this.handleFileChange}
												style={{ display: 'none' }}
											/>
										</label>
										<div className="error" style={{ display: errorMsg ? 'block' : 'none' }}>{errorMsg}</div>
										<div className="btn_wrap">
											<button type="button" className="btn_text_b btn_text_w" onClick={this.handleCancelDialog}>
												{t('common.cancel')}
											</button>
											<button type="submit" className="btn_text_b" disabled={!selectedFile}>
												{t('webfont.modal.apply')}
											</button>
										</div>
									</div>
								</div>
							</div>
						</form>
					</div>
				</div>
			</div>
		);
	}
}

const mapStateToProps = (reducers) => {
	return {
		webFontData: reducers.setting.webFontData,
	}
}

const mapDispatchToProps = (dispatch) => {
	return {
		uploadWebFont: (fontFile, localeInfo) => dispatch({
			type: types.FETCH_WEBFONT_UPLOAD_REQUEST,
			fontFile,
			localeInfo
		}),
		fetchWebFontList: () => dispatch({
			type: types.FETCH_WEBFONT_LIST_REQUEST
		}),
		deleteWebFont: (fontId) => dispatch({
			type: types.FETCH_WEBFONT_DELETE_REQUEST,
			fontId
		}),
	}
}

export default withTranslation()(connect(mapStateToProps, mapDispatchToProps)(SettingWebFont))
